import type { TenantInfo, InstanceInfo, PlmProductInfo, BundleSummary } from "../../types";

// --- Tree node shapes (data layered on top of the API info objects) ---

export interface TreeTenant {
  info: TenantInfo;
  instances?: TreeInstance[];
  loadingInstances?: boolean;
  instancesError?: string;
}

export interface TreeInstance {
  info: InstanceInfo;
  products?: TreePlmProduct[];
  loadingProducts?: boolean;
  productsError?: string;
}

export interface TreePlmProduct {
  info: PlmProductInfo;
  bundles?: BundleSummary[];
  loadingBundles?: boolean;
  bundlesError?: string;
}

// --- Per-level iconography (bee-icon names; rendered in the node prefix slot) ---

export const LEVEL_ICONS = {
  tenant: "building",
  instance: "globe",
  product: "box",
  bundle: "package",
} as const;

// --- Immutable update helpers ---
//
// The tree is held in a single @state array; mutating a nested node means
// rebuilding the spine down to it. These keep the load/CRUD handlers terse and
// out of the render code.

export function patchTenant(
  tenants: TreeTenant[],
  ci: number,
  patch: Partial<TreeTenant>,
): TreeTenant[] {
  return tenants.map((t, i) => (i === ci ? { ...t, ...patch } : t));
}

export function patchInstance(
  tenants: TreeTenant[],
  ci: number,
  ii: number,
  patch: Partial<TreeInstance>,
): TreeTenant[] {
  return tenants.map((t, i) => {
    if (i !== ci) return t;
    return { ...t, instances: t.instances?.map((inst, j) => (j === ii ? { ...inst, ...patch } : inst)) };
  });
}

export function patchPlmProduct(
  tenants: TreeTenant[],
  ci: number,
  ii: number,
  pi: number,
  patch: Partial<TreePlmProduct>,
): TreeTenant[] {
  return tenants.map((t, i) => {
    if (i !== ci) return t;
    return {
      ...t,
      instances: t.instances?.map((inst, j) => {
        if (j !== ii) return inst;
        return { ...inst, products: inst.products?.map((p, k) => (k === pi ? { ...p, ...patch } : p)) };
      }),
    };
  });
}

// --- Optimistic removal helpers (mirror the create-time optimistic inserts) ---

export function removeTenant(tenants: TreeTenant[], name: string): TreeTenant[] {
  return tenants.filter((t) => t.info.name !== name);
}

export function removeInstance(tenants: TreeTenant[], tenant: string, name: string): TreeTenant[] {
  return tenants.map((t) => {
    if (t.info.name !== tenant || !t.instances) return t;
    return { ...t, instances: t.instances.filter((inst) => inst.info.name !== name) };
  });
}

export function removePlmProduct(
  tenants: TreeTenant[],
  tenant: string,
  instance: string,
  name: string,
): TreeTenant[] {
  return tenants.map((t) => {
    if (t.info.name !== tenant || !t.instances) return t;
    return {
      ...t,
      instances: t.instances.map((inst) => {
        if (inst.info.name !== instance || !inst.products) return inst;
        return { ...inst, products: inst.products.filter((p) => p.info.name !== name) };
      }),
    };
  });
}
